import React, { useState } from 'react';
import {
  Box,
  Container,
  Tab,
  Tabs,
  Typography,
  Tooltip,
  Chip,
  Grid
} from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import FeaturedNFTCard from '../components/FeaturedNFTCard';
import CollectionList from '../components/CollectionList';
import TopMovers from '../components/TopMovers';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import RarityRanking from '../components/RarityRanking';
import PriceHistoryChart from '../components/PriceHistoryChart';
import { trendingCollections } from '../data/collections';
import 'swiper/css/effect-fade';

// Helper function to generate dynamic dates and price history
const generatePriceHistory = (basePrice, daysCount = 8) => {
  const history = [];
  const today = new Date();
  
  for (let i = daysCount - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Add some random price variation (±10% from base price)
    const variation = basePrice * (0.9 + Math.random() * 0.2);
    history.push({
      timestamp: date.toISOString().split('T')[0],
      price: Number(variation.toFixed(2))
    });
  }
  
  return history;
};

// Mock data for featured NFTs
const featuredNFTs = [
  {
    id: 1,
    title: "BEANZ Official",
    image: "https://images.mirror-media.xyz/publication-images/seVrU9RA6Hip_TX4KG0Jc.jpeg",
    price: "0.15 ETH",
    creator: "TeamAzuki",
    collectionId: "beanz",
    rarityScore: 98.5,
    rarityRank: 125,
    totalSupply: 10000,
    verified: true,
    priceHistory: generatePriceHistory(0.15)
  },
  {
    id: 2,
    title: "Pudgy Penguins",
    image: "https://unchainedcrypto.com/wp-content/uploads/2024/12/shutterstock_2343758045-scaled.jpg",
    price: "10.8 ETH",
    creator: "TheIgIoCompany",
    collectionId: "pudgypenguins",
    rarityScore: 99.2,
    rarityRank: 45,
    totalSupply: 8888,
    verified: true,
    priceHistory: generatePriceHistory(10.8)
  },
  {
    id: 3,
    title: "Doodles",
    image: "https://nftnow.com/wp-content/uploads/2021/10/Doodles-Header-scaled.jpg",
    price: "3.7 ETH",
    creator: "doodles",
    collectionId: "doodles",
    rarityScore: 97.8,
    rarityRank: 234,
    totalSupply: 10000,
    verified: true,
    priceHistory: generatePriceHistory(3.7)
  },
  {
    id: 4,
    title: "Milady Maker",
    image: "https://blockworks.co/_next/image?url=https%3A%2F%2Fblockworks-co.imgix.net%2Fwp-content%2Fuploads%2F2022%2F05%2FMilady-Maker-2.jpg&w=1920&q=75",
    price: "4.1 ETH",
    creator: "43E2E7",
    collectionId: "milady",
    rarityScore: 96.5,
    rarityRank: 456,
    totalSupply: 10000,
    verified: true,
    priceHistory: generatePriceHistory(4.1)
  },
  {
    id: 5,
    title: "Azuki",
    image: "https://i.seadn.io/gae/H8jOCJuQokNqGBpkBN5wk1oZwO7LM8bNnrHCaekV2nKjnCqw6UB5oaH8XyNeBDj6bA_n1mjejzhFQUP3O1NfjFLHr3FOaeHcTOOT?auto=format&w=1920",
    price: "13.9 ETH",
    creator: "TeamAzuki",
    collectionId: "azuki",
    rarityScore: 99.8,
    rarityRank: 12,
    totalSupply: 10000,
    verified: true,
    priceHistory: generatePriceHistory(13.9)
  },
  {
    id: 6,
    title: "Moonbirds",
    image: "https://nftnow.com/wp-content/uploads/2022/04/Moonbirds-NFT.png",
    price: "3.2 ETH",
    creator: "PROOF",
    collectionId: "moonbirds",
    rarityScore: 98.9,
    rarityRank: 89,
    totalSupply: 10000,
    verified: true,
    priceHistory: generatePriceHistory(3.2)
  },
  {
    id: 7,
    title: "Sappy Seals",
    image: "https://pbs.twimg.com/profile_banners/1427280318034235402/1681167287/1500x500",
    price: "0.68 ETH",
    creator: "SappySeals",
    collectionId: "sappyseals",
    rarityScore: 95.5,
    rarityRank: 678,
    totalSupply: 10000,
    verified: true,
    priceHistory: generatePriceHistory(0.68)
  }
];

// Using trendingCollections data instead of hardcoded collections
const collections = trendingCollections.map(collection => ({
  id: collection.id,
  name: collection.name,
  image: collection.image,
  floorPrice: collection.floorPrice.toFixed(2) + " ETH",
  totalVolume: collection.totalVolume.toFixed(2) + " ETH",
  owners: collection.owners.toString(),
  supply: collection.supply.toString()
}));

function HomePage() {
  const [activeTab, setActiveTab] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'background.default',
      color: 'text.primary',
      transition: 'all 0.3s ease-in-out',
    }}>
      <Box sx={{ 
        position: 'fixed', 
        top: 16, 
        right: 16, 
        zIndex: 1000 
      }}>
        <ThemeToggle />
      </Box>

      <Container maxWidth="xl" sx={{ flex: 1 }}>
        <Box sx={{ 
          my: 4,
          background: 'linear-gradient(180deg, rgba(0, 245, 255, 0.1) 0%, rgba(15, 22, 36, 0) 100%)',
          borderRadius: '16px',
          p: 2,
          transition: 'all 0.3s ease-in-out',
        }}>
          <Swiper
            modules={[Navigation, Pagination, Autoplay, EffectFade]}
            spaceBetween={30}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 5000 }}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
            style={{ 
              borderRadius: '16px',
              overflow: 'hidden',
              '--swiper-navigation-color': '#00F5FF',
              '--swiper-pagination-color': '#00F5FF'
            }}
          >
            {featuredNFTs.map((nft) => (
              <SwiperSlide key={nft.id}>
                <FeaturedNFTCard nft={nft} />
              </SwiperSlide>
            ))}
          </Swiper>
          {/* NFT Details Section below the slider */}
          <Box sx={{ 
            mt: 3, 
            p: 3, 
            backgroundColor: 'rgba(15, 22, 36, 0.9)',
            borderRadius: 2,
            border: '1px solid rgba(0, 245, 255, 0.2)',
          }}>
            <Grid container spacing={3}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  {/* Rarity Section */}
                  <Box sx={{ mb: 4 }}>
                    <RarityRanking 
                      score={featuredNFTs[currentSlide].rarityScore}
                      rank={featuredNFTs[currentSlide].rarityRank}
                      totalSupply={featuredNFTs[currentSlide].totalSupply}
                    />
                  </Box>
                  
                  {/* Collection Stats */}
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: 'white', 
                      mb: 2,
                      fontWeight: 600
                    }}>
                      Collection Stats
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0, 245, 255, 0.05)',
                          borderRadius: 1,
                          border: '1px solid rgba(0, 245, 255, 0.1)'
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Floor Price
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#00F5FF' }}>
                            {featuredNFTs[currentSlide].price}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0, 245, 255, 0.05)',
                          borderRadius: 1,
                          border: '1px solid rgba(0, 245, 255, 0.1)'
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            24h Volume
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#00F5FF' }}>
                            {(Math.random() * 100).toFixed(2)} ETH
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0, 245, 255, 0.05)',
                          borderRadius: 1,
                          border: '1px solid rgba(0, 245, 255, 0.1)'
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Owners
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#00F5FF' }}>
                            {Math.floor(featuredNFTs[currentSlide].totalSupply * 0.6)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0, 245, 255, 0.05)',
                          borderRadius: 1,
                          border: '1px solid rgba(0, 245, 255, 0.1)'
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Listed
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#00F5FF' }}>
                            {Math.floor(featuredNFTs[currentSlide].totalSupply * 0.05)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  {/* Price History Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ 
                      color: 'white', 
                      mb: 3,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span>Price History</span>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1 
                      }}>
                        {['24h', '7d', '30d', '90d'].map((period) => (
                          <Chip
                            key={period}
                            label={period}
                            size="small"
                            sx={{
                              backgroundColor: period === '7d' ? 'rgba(0, 245, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                              color: period === '7d' ? '#00F5FF' : 'white',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 245, 255, 0.3)',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Typography>
                    <Box sx={{ 
                      height: 250,
                      backgroundColor: 'transparent'
                    }}>
                      <PriceHistoryChart 
                        data={featuredNFTs[currentSlide].priceHistory} 
                      />
                    </Box>
                  </Box>

                  {/* Market Activity */}
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: 'white', 
                      mb: 2,
                      fontWeight: 600
                    }}>
                      Recent Activity
                    </Typography>
                    <Box sx={{ 
                      maxHeight: 150,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0, 245, 255, 0.3)',
                        borderRadius: '4px',
                      },
                    }}>
                      {[...Array(5)].map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            '&:last-child': {
                              borderBottom: 'none',
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mr: 1 }}>
                              {index % 2 === 0 ? 'Sale' : 'List'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#00F5FF' }}>
                              #{Math.floor(Math.random() * 10000)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            {(Math.random() * 5).toFixed(2)} ETH
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <TopMovers collections={trendingCollections} />

        <Box sx={{ my: 6 }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            mb: 3,
            pb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Collections
            </Typography>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                },
                '& .MuiTab-root': {
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                },
              }}
            >
              <Tab 
                label="Trending" 
                icon={
                  <Tooltip title="Trending collections based on volume">
                    <Chip 
                      label="Live" 
                      size="small" 
                      sx={{ 
                        backgroundColor: 'secondary.main',
                        color: 'white',
                        height: '20px',
                        ml: 1
                      }} 
                    />
                  </Tooltip>
                }
              />
              <Tab label="Top" />
            </Tabs>
          </Box>
          <CollectionList activeTab={activeTab} />
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}

export default HomePage;
